import { useReactTable, getCoreRowModel, flexRender } from '@tanstack/react-table';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '~/shadcn/components/ui/Table';
import { FBMOrderIngestResultsEndpoint } from '../../../../api/fbmOrderIngestResultsEndpoint';
import { TableCaption } from '../../../../shadcn/components/ui/Table';
import { Button } from '../../../../shadcn/components/ui/Button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../shadcn/components/ui/Select';
import { UserContext } from '../../../../contexts/UserContext';
import { Notifications } from '../../../../shared';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { Progress } from '../../../../shadcn/components/ui/Progress';
import dayjs from '~/utils/dayjsConfig';
import { Switch } from '../../../../shadcn/components/ui/Switch';

const ViewAllFBMIngestResults = () => {
    // Api
    const fbmOrderIngestResultsEndpoint = new FBMOrderIngestResultsEndpoint();

    // State
    const { fbmAccount } = useContext(UserContext);
    const [data, setData] = useState([]);
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 25,
    });
    const [notification, setNotification] = useState({ text: null, level: null });
    const [isLoading, setIsLoading] = useState(false);
    const [includeSuccess, setIncludeSuccess] = useState(false);
    const columns = useMemo(
        () => [
            {
                header: 'ID',
                accessorKey: 'id',
            },
            {
                header: `Created Time (${dayjs().tz(dayjs.tz.guess()).format('z')})`,
                accessorFn: (row) => dayjs(row.createdAt).format('MM/DD/YYYY h:mm a'),
            },
            {
                header: 'FBM Order Number',
                accessorKey: 'fbmOrderNum',
            },
            {
                header: 'Type',
                accessorKey: 'type',
            },
            {
                header: 'Details',
                accessorFn: (row) => {
                    return 'Coming Soon';
                },
            },
        ],
        [],
    );

    // Effects
    useEffect(() => {
        handleFetchPage(pagination.pageIndex, pagination.pageSize, fbmAccount.fbmAccountName, includeSuccess);
    }, [pagination, fbmAccount.fbmAccountName, includeSuccess]);

    // Handlers
    const handleOnPaginationChange = async (update) => {
        setPagination(update);
    };

    const handleFetchPage = useCallback(async (page, pageSize, fbmAccountName, includeSuccess) => {
        // TODO: handle multiple pages loading at the same time
        setIsLoading(true);
        const results = await fbmOrderIngestResultsEndpoint.getOrderIngestResults(
            fbmAccountName,
            includeSuccess,
            page,
            pageSize,
        );

        if (results.status) {
            setData(results.data.ingestResults);
        } else {
            setNotification({ text: results.msg, level: 'error' });
        }
        setIsLoading(false);
    }, []);

    const handleIncludeSuccessChange = (value) => {
        setIncludeSuccess(value);
    };

    // Table
    const table = useReactTable({
        columns,
        data,
        getCoreRowModel: getCoreRowModel(),
        manualPagination: true,
        pageCount: -1, // Don't know page count so set to -1
        state: {
            pagination,
        },
        onPaginationChange: handleOnPaginationChange,
    });

    return (
        <>
            <Notifications options={notification} />
            <div className="flex items-center gap-2 mb-2">
                <Switch checked={includeSuccess} onCheckedChange={handleIncludeSuccessChange} />
                <span className="text-sm text-muted-foreground">Include Success</span>
            </div>
            <div className="h-1">{isLoading && <Progress value={null} className="h-full" />}</div>
            <div className="rounded-md border">
                <Table>
                    <TableHeader>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <TableHead key={header.id}>
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(header.column.columnDef.header, header.getContext())}
                                    </TableHead>
                                ))}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {table.getRowModel().rows?.length ? (
                            table.getRowModel().rows.map((row) => (
                                <TableRow key={row.id}>
                                    {row.getVisibleCells().map((cell) => (
                                        <TableCell key={cell.id} className="text-left">
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={columns.length} className="h-24 text-center">
                                    No results.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                    <TableCaption>
                        <div className="flex items-center justify-between gap-4 px-2">
                            <div className="flex items-center gap-2">
                                <span className="text-sm text-muted-foreground">Rows per page:</span>
                                <Select
                                    value={`${table.getState().pagination.pageSize}`}
                                    onValueChange={(value) => {
                                        table.setPageSize(Number(value));
                                    }}
                                    disabled={isLoading}
                                >
                                    <SelectTrigger className="h-8 w-[70px]">
                                        <SelectValue placeholder={table.getState().pagination.pageSize} />
                                    </SelectTrigger>
                                    <SelectContent side="top">
                                        {[10, 25, 50, 100].map((pageSize) => (
                                            <SelectItem key={pageSize} value={`${pageSize}`}>
                                                {pageSize}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="flex items-center gap-2 mb-2">
                                <Button
                                    variant="outline"
                                    size="icon"
                                    onClick={() => table.setPageIndex(0)}
                                    disabled={!table.getCanPreviousPage() || isLoading}
                                >
                                    <ChevronsLeft className="h-4 w-4" />
                                </Button>
                                <Button
                                    variant="outline"
                                    size="icon"
                                    onClick={() => table.previousPage()}
                                    disabled={!table.getCanPreviousPage() || isLoading}
                                >
                                    <ChevronLeft className="h-4 w-4" />
                                </Button>
                                <span className="text-sm text-muted-foreground">
                                    Page {table.getState().pagination.pageIndex + 1}
                                </span>
                                <Button
                                    variant="outline"
                                    size="icon"
                                    onClick={() => table.nextPage()}
                                    disabled={!table.getCanNextPage() || isLoading}
                                >
                                    <ChevronRight className="h-4 w-4" />
                                </Button>
                                {/* TODO: implement a go to last page, this comes with the page info endpoint / meta data */}
                            </div>
                        </div>
                    </TableCaption>
                </Table>
            </div>
        </>
    );
};

export { ViewAllFBMIngestResults };
